import * as Dialog from '@radix-ui/react-dialog';
import { ArrowRight, Check, X, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';

import { usePublicPlans, type PublicPlan } from '@/shared/api/billing';
import common from '@/shared/i18n/en/common';
import messages from '@/shared/i18n/en/billing-upgrade';
import { useT } from '@/shared/i18n/react';
import { tr } from '@/shared/i18n/translate';
import { fmtMoney } from '@/shared/lib/format-locale';

import { formatBytes } from './format-bytes';

/**
 * Global upgrade dialog opened by `MutationCache.onError` whenever a
 * mutation fails with a `FREE_TIER_*` error code.  The handler emits a
 * `projoint:upgrade-prompt` CustomEvent on `window`; this component
 * listens for it.
 *
 * Detail payload:
 *   {
 *     limitCode: 'FREE_TIER_USERS' | 'FREE_TIER_PROJECTS' | 'FREE_TIER_STORAGE' | 'FREE_TIER_FILE_SIZE',
 *     planCode: string,   // current customer plan (e.g. 'free')
 *     current:  number,
 *     max:      number,
 *   }
 *
 * Decoupled from React Query so the trigger lives in main.tsx without
 * cross-importing widget code.
 */

export interface UpgradePromptDetail {
  limitCode: 'FREE_TIER_USERS' | 'FREE_TIER_PROJECTS' | 'FREE_TIER_STORAGE' | 'FREE_TIER_FILE_SIZE';
  planCode: string;
  current: number;
  max: number;
}

const EVENT_NAME = 'projoint:upgrade-prompt';

export function dispatchUpgradePrompt(detail: UpgradePromptDetail): void {
  window.dispatchEvent(new CustomEvent<UpgradePromptDetail>(EVENT_NAME, { detail }));
}

export function UpgradePromptHost() {
  const [detail, setDetail] = useState<UpgradePromptDetail | null>(null);
  const { data: plans } = usePublicPlans();
  const t = useT(messages);
  const tc = useT(common);

  useEffect(() => {
    function onPrompt(e: Event) {
      const evt = e as CustomEvent<UpgradePromptDetail>;
      setDetail(evt.detail);
    }
    window.addEventListener(EVENT_NAME, onPrompt);
    return () => window.removeEventListener(EVENT_NAME, onPrompt);
  }, []);

  if (!detail) return null;

  return (
    <Dialog.Root open={detail !== null} onOpenChange={(o) => !o && setDetail(null)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-xl bg-[var(--bg)] p-6 shadow-2xl ring-1 ring-[var(--border)]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-[var(--accent-subtle)] px-2 py-0.5 text-xs font-medium text-[var(--accent)] ring-1 ring-[var(--accent)]/30">
                <Zap size={12} /> {t('limitReachedBadge')}
              </div>
              <Dialog.Title className="text-lg font-semibold text-[var(--fg)]">
                {titleFor(detail.limitCode)}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-[var(--fg-subtle)]">
                {descriptionFor(detail)}
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label={tc('close')}
              className="grid size-8 place-items-center rounded-md text-[var(--fg-muted)] hover:bg-[var(--bg-muted)] hover:text-[var(--fg)]"
            >
              <X size={18} />
            </Dialog.Close>
          </div>

          {/* Plan comparison — show plans above the current */}
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {(plans ?? [])
              .filter((p) => isUpgradeFrom(detail.planCode, p.code))
              .map((p) => (
                <PlanCard key={p.code} plan={p} highlight={p.code === 'team'} />
              ))}
          </div>

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-[var(--border)] pt-4">
            <Dialog.Close className="rounded-md px-3 py-1.5 text-sm text-[var(--fg-subtle)] hover:bg-[var(--bg-muted)]">
              {t('maybeLater')}
            </Dialog.Close>
            <a
              href="/account/billing"
              onClick={() => setDetail(null)}
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-4 py-1.5 text-sm font-medium text-[var(--accent-fg)] hover:bg-[var(--accent-hover)]"
            >
              {t('manageSubscription')} <ArrowRight size={14} />
            </a>
          </div>

          <p className="mt-3 text-xs text-[var(--fg-muted)]">
            {t('stripeSoon')}
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function titleFor(code: UpgradePromptDetail['limitCode']): string {
  switch (code) {
    case 'FREE_TIER_USERS':
      return tr(messages, 'memberLimitTitle');
    case 'FREE_TIER_PROJECTS':
      return tr(messages, 'projectLimitTitle');
    case 'FREE_TIER_STORAGE':
      return tr(messages, 'storageLimitTitle');
    case 'FREE_TIER_FILE_SIZE':
      return tr(messages, 'fileTooLargeTitle');
  }
}

function descriptionFor(d: UpgradePromptDetail): string {
  switch (d.limitCode) {
    case 'FREE_TIER_USERS':
      return tr(messages, 'memberLimitBody', { max: d.max, current: d.current });
    case 'FREE_TIER_PROJECTS':
      return tr(messages, 'projectLimitBody', { max: d.max, current: d.current });
    case 'FREE_TIER_STORAGE':
      return tr(messages, 'storageLimitBody', { max: formatBytes(d.max), current: formatBytes(d.current) });
    case 'FREE_TIER_FILE_SIZE':
      return tr(messages, 'fileTooLargeBody', { current: formatBytes(d.current), max: formatBytes(d.max) });
  }
}

function isUpgradeFrom(currentPlan: string, candidate: string): boolean {
  // Free → suggest personal_pro + team.  Personal Pro → suggest team.  Team → nothing.
  const order: Record<string, number> = { free: 0, personal_pro: 1, team: 2, enterprise: 3 };
  return (order[candidate] ?? 99) > (order[currentPlan] ?? 0);
}

function PlanCard({ plan, highlight }: { plan: PublicPlan; highlight?: boolean }) {
  const t = useT(messages);
  const features: { label: string; included: boolean | string }[] = [
    {
      label: t('featMembers'),
      included: plan.maxMembers === null ? t('unlimited') : t('upTo', { n: plan.maxMembers }),
    },
    {
      label: t('featProjects'),
      included: plan.maxProjects === null ? t('unlimited') : t('upTo', { n: plan.maxProjects }),
    },
    {
      label: t('featStorage'),
      included:
        plan.storageQuotaPerSeatBytes !== null
          ? t('perSeat', { size: formatBytes(plan.storageQuotaPerSeatBytes) })
          : formatBytes(plan.storageQuotaBytes),
    },
    {
      label: t('featMaxFileSize'),
      included: formatBytes(plan.maxFileSizeBytes),
    },
    ...(plan.features.github_integration ? [{ label: t('featGithub'), included: true }] : []),
    ...(plan.features.custom_roles ? [{ label: t('featCustomRoles'), included: true }] : []),
    ...(plan.features.audit_log ? [{ label: t('featAuditLog'), included: true }] : []),
  ];

  return (
    <div
      className={
        highlight
          ? 'rounded-lg border-2 border-[var(--accent)] bg-[var(--accent-subtle)] p-4'
          : 'rounded-lg border border-[var(--border)] bg-[var(--bg-subtle)] p-4'
      }
    >
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-base font-semibold text-[var(--fg)]">{plan.name}</h3>
        <div className="text-right">
          <div className="text-lg font-semibold text-[var(--fg)]">
            {fmtMoney(plan.monthlyPriceCents / 100, 'USD', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
          <div className="text-xs text-[var(--fg-muted)]">{t('slashMonth')}</div>
        </div>
      </div>
      <ul className="space-y-1 text-sm text-[var(--fg-subtle)]">
        {features.map((f, idx) => (
          <li key={idx} className="flex items-baseline gap-1.5">
            <Check size={12} className="mt-0.5 shrink-0 text-[var(--success-fg)]" />
            <span>
              {typeof f.included === 'string' ? (
                <>
                  <span className="text-[var(--fg-muted)]">{t('featLabel', { label: f.label })}</span>{' '}
                  <span className="font-medium">{f.included}</span>
                </>
              ) : (
                f.label
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
